import React, { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Search, ChevronDown, ChevronUp, Check, Shield, Clock, Database, FileText } from 'lucide-react'
import ReactJsonView from 'react-json-view'

import { DSAPMockServer, Scenario, DSAPRequest, DSAPResponse } from '../mock/dsap-mock'

interface Props {
  onComplete: (data: any) => void
  onScenarioSelect: (scenario: Scenario) => void
  selectedScenario: Scenario | null
  mockServer: DSAPMockServer
  addToHistory: (request: DSAPRequest, response: DSAPResponse) => void
}

export default function Step1Discover({
  onComplete,
  onScenarioSelect,
  selectedScenario,
  mockServer,
  addToHistory
}: Props) {
  const [loading, setLoading] = useState(false)
  const [agentCard, setAgentCard] = useState<any>(null)
  const [showAgentCardDetails, setShowAgentCardDetails] = useState(false)
  const [scenarios] = useState(mockServer.getScenarios())

  const handleDiscoverServer = useCallback(async () => {
    setLoading(true)
    
    try {
      const request: DSAPRequest = {
        jsonrpc: '2.0',
        method: 'dsap.discover',
        params: {},
        id: Date.now()
      }
      
      const response = await mockServer.processRequest(request)
      addToHistory(request, response)
      
      if (response.result) {
        setAgentCard(response.result)
        // Auto-expand details after discovery
        setTimeout(() => setShowAgentCardDetails(true), 500)
      }
    } catch (error) {
      console.error('Discovery failed:', error)
    } finally {
      setLoading(false)
    }
  }, [mockServer, addToHistory])

  const handleContinue = useCallback(() => {
    onComplete({ agentCard })
  }, [onComplete, agentCard])

  const capabilities = agentCard?.agentCard?.capabilities

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-dsap-100 rounded-lg flex items-center justify-center">
              <Search className="h-6 w-6 text-dsap-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Step 1: Discover DSAP Server Capabilities
            </h3>
            <p className="text-gray-600 mb-4">
              Connect to the DSAP server and learn about its security capabilities, supported 
              regulations, and available features. This is like the "handshake" that establishes 
              what data protection services are available.
            </p>
            
            {!selectedScenario && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  💡 <strong>Start by selecting a scenario below</strong> to see DSAP in action
                  with realistic data access patterns.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scenario Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Choose a Demo Scenario
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {scenarios.map((scenario) => (
            <motion.button
              key={scenario.id}
              onClick={() => onScenarioSelect(scenario)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                selectedScenario?.id === scenario.id
                  ? 'border-dsap-500 bg-dsap-50'
                  : 'border-gray-200 hover:border-dsap-300 hover:bg-gray-50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900">{scenario.name}</h5>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  scenario.expectedOutcome === 'granted' ? 'bg-green-100 text-green-800' :
                  scenario.expectedOutcome === 'denied' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {scenario.expectedOutcome}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">{scenario.description}</p>
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <span>Risk: {scenario.expectedRiskScore}/100</span>
                <span>•</span>
                <span>{scenario.context.sensitivity.join(', ')}</span>
              </div>
              {selectedScenario?.id === scenario.id && (
                <div className="mt-3 flex items-center text-dsap-600 text-sm font-medium">
                  <Check className="h-4 w-4 mr-1" />
                  Selected
                </div>
              )}
            </motion.button>
          ))}
        </div>
        
        {selectedScenario && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">Selected Scenario Details</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-600 font-medium">Agent:</span>
                <br />
                {selectedScenario.agentId}
              </div>
              <div>
                <span className="text-blue-600 font-medium">User:</span>
                <br />
                {selectedScenario.userId}
              </div>
              <div>
                <span className="text-blue-600 font-medium">Resource:</span>
                <br />
                {selectedScenario.dataResource}
              </div>
              <div>
                <span className="text-blue-600 font-medium">Operation:</span>
                <br />
                {selectedScenario.operation}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Server Discovery */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Server Capability Discovery
        </h4>
        
        {!agentCard ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-dsap-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-dsap-600" />
            </div>
            <h5 className="text-lg font-medium text-gray-900 mb-2">
              Ready to Discover DSAP Server
            </h5>
            <p className="text-gray-600 mb-6">
              Click below to connect to the DSAP server and learn about its capabilities.
            </p>
            <button
              onClick={handleDiscoverServer}
              disabled={loading || !selectedScenario}
              className={`inline-flex items-center px-6 py-3 text-base font-medium rounded-lg transition-all ${
                !selectedScenario
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : loading
                  ? 'bg-dsap-400 text-white cursor-wait'
                  : 'bg-dsap-600 text-white hover:bg-dsap-700 transform hover:scale-105'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Discovering Server...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" />
                  Discover Server Capabilities
                </>
              )}
            </button>
            {!selectedScenario && (
              <p className="text-sm text-gray-500 mt-2">
                Please select a scenario first
              </p>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Server Overview */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <Shield className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-green-900">
                    {agentCard.agentCard.name}
                  </h5>
                  <p className="text-sm text-green-600">
                    {agentCard.agentCard.vendor} • v{agentCard.agentCard.version}
                  </p>
                </div>
              </div>
              <p className="text-sm text-green-800">
                {agentCard.agentCard.description}
              </p>
            </div>

            {/* Capability Highlights */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-blue-900">Access Control</div>
                <div className="text-xs text-blue-600">
                  {capabilities?.accessVerification?.responseTime}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <FileText className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-purple-900">Compliance</div>
                <div className="text-xs text-purple-600">
                  {agentCard.agentCard.supportedRegulations?.length} regulations
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <Database className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-green-900">Audit Logging</div>
                <div className="text-xs text-green-600">
                  {capabilities?.auditLogging?.retentionPeriod}
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <Search className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                <div className="text-sm font-medium text-orange-900">Classification</div>
                <div className="text-xs text-orange-600">
                  {capabilities?.dataClassification?.autoClassification ? 'Auto' : 'Manual'}
                </div>
              </div>
            </div>

            {/* Supported Regulations */}
            <div className="mb-6">
              <h5 className="font-medium text-gray-900 mb-3">Supported Regulations</h5>
              <div className="flex flex-wrap gap-2">
                {agentCard.agentCard.supportedRegulations?.map((reg: string) => (
                  <span
                    key={reg}
                    className="status-pill status-pill-audit"
                  >
                    {reg}
                  </span>
                ))}
              </div>
            </div>

            {/* Agent Card JSON */}
            <div className="mb-6">
              <button
                onClick={() => setShowAgentCardDetails(!showAgentCardDetails)}
                className="flex items-center space-x-2 text-dsap-600 hover:text-dsap-700 font-medium"
              >
                {showAgentCardDetails ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                <span>
                  {showAgentCardDetails ? 'Hide' : 'Show'} Complete Agent Card JSON
                </span>
              </button>
              
              {showAgentCardDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <ReactJsonView
                    src={agentCard}
                    theme="monokai"
                    collapsed={2}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    name="agentCard"
                  />
                </motion.div>
              )}
            </div>

            {/* Continue Button */}
            <div className="flex justify-end">
              <button
                onClick={handleContinue}
                className="inline-flex items-center px-6 py-3 bg-dsap-600 text-white font-medium rounded-lg hover:bg-dsap-700 transition-colors"
              >
                Continue to Access Verification
                <ChevronDown className="h-4 w-4 ml-2 rotate-[-90deg]" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}